import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { apiClient } from "../API/axiosInstance";
import { CONFIG } from "../config";
import { logger } from "../utils/logger";

// Paths that should be excluded from billing checks
const BILLING_EXEMPT_PATHS = [
  "/billing/limit-reached",
  "/billing/register-card",
  "/billing/invoice-due",
  "/billing/success",
  "/billing/cancel",
  "/billing/subscription-success",
  "/account",
];

// Cache billing status to avoid excessive API calls
// but still check on each page navigation
let billingCache = {
  status: null, // 'ok', 'card_required', 'limit_reached', 'billing_inactive'
  timestamp: 0,
};
const CACHE_DURATION_MS = 5000; // 5 seconds - short enough to catch changes, long enough to prevent spam

/**
 * Hook to check billing status on page load
 * Redirects users to appropriate billing pages if they have billing issues
 */
export function useBillingCheck() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth0();
  const checkInProgressRef = useRef(false);

  useEffect(() => {
    // Skip if billing is disabled
    if (!CONFIG.billing_enabled) return;

    // Skip if still loading auth
    if (isLoading) return;

    // Skip if not authenticated
    if (!isAuthenticated) return;

    // Skip if on exempt paths
    if (BILLING_EXEMPT_PATHS.includes(location.pathname)) return;

    // Skip if a check is already in progress
    if (checkInProgressRef.current) return;

    // Check if we have a recent cached billing issue - if so, redirect immediately
    const now = Date.now();
    const cacheValid = now - billingCache.timestamp < CACHE_DURATION_MS;

    if (cacheValid && billingCache.status) {
      if (
        billingCache.status === "card_required" ||
        billingCache.status === "billing_inactive"
      ) {
        navigate("/billing/register-card", { replace: true });
        return;
      }
      if (billingCache.status === "limit_reached") {
        navigate("/billing/limit-reached", { replace: true });
        return;
      }
      // If status is 'ok', skip the API call
      if (billingCache.status === "ok") {
        return;
      }
    }

    const checkBilling = async () => {
      checkInProgressRef.current = true;

      try {
        const response = await apiClient.get("/billing/check");
        const data = response.data;

        if (!data) {
          billingCache = { status: "ok", timestamp: Date.now() };
          checkInProgressRef.current = false;
          return;
        }

        // Handle billing status messages
        if (
          data.message === "card_required" ||
          data.message === "billing_inactive"
        ) {
          billingCache = { status: data.message, timestamp: Date.now() };
          navigate("/billing/register-card", { replace: true });
          return;
        }

        if (data.message === "limit_reached") {
          billingCache = { status: "limit_reached", timestamp: Date.now() };
          navigate("/billing/limit-reached", { replace: true });
          return;
        }

        // Billing is OK
        billingCache = { status: "ok", timestamp: Date.now() };
      } catch (error) {
        logger.error("Billing check failed:", error);
      } finally {
        checkInProgressRef.current = false;
      }
    };

    checkBilling();
  }, [location.pathname, isAuthenticated, isLoading, navigate]);
}
